import {
  AfterViewInit,
  Component,
  computed,
  CUSTOM_ELEMENTS_SCHEMA,
  DestroyRef,
  ElementRef,
  inject,
  Injector,
  input,
  linkedSignal,
  signal,
  viewChild,
} from '@angular/core';
import {
  ModalController,
  IonContent,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButton,
  IonIcon,
  IonInput,
  IonListHeader,
  IonList,
  IonItem,
  IonLabel,
  IonNote,
  IonSegment,
  IonSegmentButton,
} from '@ionic/angular/standalone';
import { OperationService } from 'src/app/features/mce-editor/extensions/operation/operation.service';
import {
  allStatInOperationExistInSheet,
  getReferenceFromStatPath,
  getStatPathFromReference,
} from '../../utils/sheet-operations-merge';
import {
  formatInsertSummaryDisplay,
  formatStatChoiceDisplay,
  getInsertFieldLabelKey,
  getInsertPhaseHintKey,
  humanizeFieldName,
  parseChoiceDisplay,
  resolveInsertHintText,
} from '../../utils/insert-step-copy';
import { Character } from 'src/app/features/characters/types/character';
import { SwiperContainer } from 'swiper/element';
import { take, timer } from 'rxjs';

import { getElementByPath } from 'src/app/features/characters/utils/sheetParser';

import { CompileStatComponent } from '../compile-stat/compile-stat.component';
import { TRANSLATE_IMPORTS } from 'src/app/features/i18n/translate-imports';
import { FormsModule } from '@angular/forms';
import {
  ReferenceToSheetFragment,
  NamedOperation,
  parseOperation,
  parseModifiers,
  getHintForField,
  iterateAllReferenceToSheet,
  iterateAllInsert,
  updateAllReferenceToSheet,
  updateAllInsert,
  ValueFragment,
  stringifyOperation,
  ChaosFragment,
  Fragment,
  Modifiers,
} from 'operationlib';
import { TranslateService } from '@ngx-translate/core';
import { Operation } from '../../types/operation';
import {
  ChaosModalReturnValue,
  GenerateChaosComponent,
} from 'src/app/features/chaos/components/generate-chaos/generate-chaos.component';
import { PosthogService } from 'src/app/features/analytics/posthog.service';
import { createNestedDialogModalOptions } from 'src/app/utils/dialog-modal';

type InsertFragmentMeta = {
  value: number;
  phase: 'creation' | 'execution';
  fieldName?: string;
  hint?: string;
  insertIndex: number;
  insertTotal: number;
};

type InsertSlide = {
  value?: number;
  chaos?: string;
  type?: 'chaos' | 'value';
  phase: 'creation' | 'execution';
  fieldName?: string;
  hint?: string;
  insertIndex: number;
  insertTotal: number;
};

@Component({
  selector: 're-select-operation',
  imports: [
    FormsModule,
    IonNote,
    IonLabel,
    IonItem,
    IonListHeader,
    IonList,
    IonIcon,
    IonContent,
    IonHeader,
    IonToolbar,
    IonTitle,
    IonButton,
    IonInput,
    IonSegment,
    IonSegmentButton,
    CompileStatComponent,
    ...TRANSLATE_IMPORTS,
  ],
  templateUrl: './select-operation.component.html',
  styleUrls: [
    './select-operation.component.scss',
    '../../styles/operation-sheet-list.scss',
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class SelectOperationComponent implements AfterViewInit {
  swiper = viewChild.required<ElementRef<SwiperContainer>>('swiper');
  modalController = inject(ModalController);
  private destroyRef = inject(DestroyRef);
  private translate = inject(TranslateService);
  private posthog = inject(PosthogService);

  operationService = inject(OperationService);
  impersonateChar = input<Character | undefined>();

  injector = inject(Injector);

  readonly humanizeFieldName = humanizeFieldName;

  fragmentsInsertToInsert = signal<InsertFragmentMeta[]>([]);
  fragmentsSelectStatToInsert = signal<
    {
      path: string[];
      index: number;
      fragment: ReferenceToSheetFragment;
    }[]
  >([]);
  slidesInsert = signal<InsertSlide[]>([]);
  slidesSelectStat = signal<
    {
      path: string[];
      index: number;
      fragment: ReferenceToSheetFragment;
    }[]
  >([]);

  slidesInsertToggle = linkedSignal(
    () => {
      return this.slidesInsert().map(() => signal<'value' | 'chaos'>('value'));
    },
    {
      equal: (a, b) => {
        return a.length === b.length;
      },
    },
  );

  activeSlideIndex = signal(0);

  storyOperations = input<Operation[]>([]);

  /** When set, skips the operation list and starts the roll flow for this definition. */
  masterSuggestion = input<{ name: string; definition: string } | undefined>(
    undefined,
  );

  operations = computed<(NamedOperation & { disabled: boolean })[]>(() =>
    this.storyOperations().map((el) => {
      return {
        name: el.name ?? '',
        operation: el.definition ?? ';',
        disabled: !allStatInOperationExistInSheet(
          parseOperation((el.definition ?? ';').split(';')[0]),
          this.impersonateChar(),
        ),
      };
    }),
  );
  characterOperations = computed<(NamedOperation & { disabled: boolean })[]>(
    () =>
      (this.impersonateChar()?.operations.map((el) => ({
        name: el.name,
        operation: el.definition,
        disabled: !allStatInOperationExistInSheet(
          parseOperation((el.definition ?? '0;').split(';')[0]),
          this.impersonateChar(),
        ),
      })) ?? []) as (NamedOperation & { disabled: boolean })[],
  );

  hasAnyOperations = computed(
    () =>
      this.characterOperations().length > 0 || this.operations().length > 0,
  );

  selectedOperation = signal<NamedOperation | undefined>(undefined);
  operationModifiers = signal<Modifiers[]>([]);
  originalReference = signal<Record<number, string[]>>({});
  updatedReference = signal<Record<number, string[]>>({});

  totalWizardSteps = computed(
    () =>
      1 + this.slidesSelectStat().length + this.slidesInsert().length + 1,
  );

  wizardProgress = computed(() => {
    const step = this.activeSlideIndex() + 1;
    const total = this.totalWizardSteps();
    return {
      step,
      total,
      operationName: this.selectedOperation()?.name ?? '',
    };
  });

  toolbarTitleKey = computed(() => {
    const idx = this.activeSlideIndex();
    const statN = this.slidesSelectStat().length;
    const insertN = this.slidesInsert().length;
    if (idx === 0) return 'operations.selectRoll.title';
    if (idx >= 1 && idx <= statN) return 'operations.selectRoll.pickStatTitle';
    if (idx > statN && idx <= statN + insertN) {
      return 'operations.selectRoll.insertStepTitle';
    }
    return 'operations.selectRoll.summaryTitle';
  });

  canGoBack = computed(() => this.activeSlideIndex() > 0);

  statLabelForFragment(fragment: ReferenceToSheetFragment): string {
    const name = fragment.name;
    if (name?.trim()) return humanizeFieldName(name);
    return '';
  }

  /** Summary shown on the final step of the sheet flow. */
  finalizeSummary = computed(() => {
    const op = this.selectedOperation();
    if (!op?.name) return null;
    const char = this.impersonateChar();
    const statSlots = this.fragmentsSelectStatToInsert();
    const insertSlots = this.slidesInsert();
    const refs = this.updatedReference();
    const statChoices = statSlots
      .map((slot) =>
        formatStatChoiceDisplay(slot.fragment, refs[slot.index], char),
      )
      .filter((s) => s.length > 0);
    const insertDisplays = insertSlots.map((s) =>
      formatInsertSummaryDisplay(s),
    );
    const choices = [...statChoices, ...insertDisplays];
    const choiceRows = choices.map(parseChoiceDisplay);
    return {
      operationName: op.name,
      characterName: char?.name,
      statChoices,
      insertDisplays,
      choices,
      choiceRows,
    };
  });

  ngAfterViewInit() {
    const swiperEl = this.swiper().nativeElement;
    const resizeObserver = new ResizeObserver(() => {
      swiperEl.swiper?.update();
    });
    resizeObserver.observe(swiperEl);
    this.destroyRef.onDestroy(() => resizeObserver.disconnect());

    const preset = this.masterSuggestion();
    const char = this.impersonateChar();
    if (!preset || !char) return;
    timer(0)
      .pipe(take(1))
      .subscribe(() => {
        this.selectOperation({
          name: preset.name,
          operation: preset.definition,
        });
      });
  }

  onSlideChange(ev: Event) {
    const swiper = (ev.target as SwiperContainer).swiper;
    this.activeSlideIndex.set(swiper.activeIndex);
  }

  goBack() {
    this.swiper().nativeElement.swiper.slidePrev();
  }

  private collectInsertFragments(
    fragments: Fragment[],
    modifiers: Modifiers[],
  ): InsertFragmentMeta[] {
    const items: Omit<InsertFragmentMeta, 'insertIndex' | 'insertTotal'>[] =
      [];
    iterateAllInsert(fragments, 'creation', (fragment) => {
      items.push({
        value: 0,
        phase: 'creation',
        fieldName: fragment.name,
        hint: getHintForField(modifiers, fragment.name),
      });
    });
    iterateAllInsert(fragments, 'execution', (fragment) => {
      items.push({
        value: 0,
        phase: 'execution',
        fieldName: fragment.name,
        hint: getHintForField(modifiers, fragment.name),
      });
    });
    const total = items.length;
    return items.map((item, i) => ({
      ...item,
      insertIndex: i,
      insertTotal: total,
    }));
  }

  private slideFromInsertMeta(meta: InsertFragmentMeta): InsertSlide {
    return {
      value: meta.value,
      type: 'value',
      phase: meta.phase,
      fieldName: meta.fieldName,
      hint: meta.hint,
      insertIndex: meta.insertIndex,
      insertTotal: meta.insertTotal,
    };
  }

  insertFieldLabelKey(slide: InsertSlide): string {
    return getInsertFieldLabelKey(slide.fieldName);
  }

  insertPhaseHintKey(slide: InsertSlide): string {
    return getInsertPhaseHintKey(slide.phase);
  }

  insertHintDisplay(slide: InsertSlide): string {
    const resolved = resolveInsertHintText(slide.hint, (key) =>
      this.translate.instant(key),
    );
    if (resolved) return resolved;
    return this.translate.instant('operations.selectRoll.insertIntro');
  }

  statHintDisplay(fragment: ReferenceToSheetFragment): string {
    const hint = getHintForField(this.operationModifiers(), fragment.name);
    return resolveInsertHintText(hint, (key) => this.translate.instant(key));
  }

  private advanceSwiper() {
    timer(0)
      .pipe(take(1))
      .subscribe(() => {
        this.swiper().nativeElement.swiper.once('update', () => {
          this.swiper().nativeElement.swiper.slideNext();
        });
        this.swiper().nativeElement.swiper.update();
      });
  }

  selectOperation(value: NamedOperation) {
    this.posthog.event('roll_operation_select', {
      operationName: value.name,
      characterId: this.impersonateChar()?.id,
      source: this.masterSuggestion() ? 'master_suggestion' : 'character_ops',
    });
    let _val = structuredClone(value);
    this.selectedOperation.set({ ..._val, compiledOperation: '' });
    const parts = _val.operation.split(';');
    let fragments = parseOperation(parts[0]);
    const modifiers = parseModifiers(parts[1] ?? '');
    this.operationModifiers.set(modifiers);
    this.originalReference.set({});
    this.updatedReference.set({});
    this.fragmentsSelectStatToInsert.set([]);
    this.slidesSelectStat.set([]);
    const inserts = this.collectInsertFragments(fragments, modifiers);
    this.fragmentsInsertToInsert.set(inserts);
    this.slidesInsert.set([]);

    iterateAllReferenceToSheet(fragments, (fragment, index) => {
      let path = getStatPathFromReference(fragment.value);
      let variantIndex = path.findIndex(
        (v) => v.includes('@') || v.includes('*'),
      );
      let element = getElementByPath(
        path.slice(0, variantIndex === -1 ? undefined : variantIndex),
        this.impersonateChar(),
      );
      if (
        element?.type === 'arrayMixed' ||
        element?.type === 'page' ||
        element?.type === 'group'
      ) {
        this.fragmentsSelectStatToInsert.update((val) => [
          ...val,
          {
            fragment,
            path,
            index,
          },
        ]);
        this.originalReference.update((val) => ({ ...val, [index]: path }));
      } else {
        this.originalReference.update((val) => ({ ...val, [index]: path }));
        this.updatedReference.update((val) => ({ ...val, [index]: path }));
      }
    });

    if (this.fragmentsSelectStatToInsert().length > 0) {
      this.slidesSelectStat.set([this.fragmentsSelectStatToInsert()[0]]);
    } else if (inserts.length > 0) {
      this.slidesInsert.set([this.slideFromInsertMeta(inserts[0])]);
    }
    this.advanceSwiper();
  }

  compiledStat(index: number, path: string[]) {
    this.updatedReference.update((val) => ({ ...val, [index]: path }));
    if (this.fragmentsSelectStatToInsert().find((el) => el.index > index)) {
      let stop = this.populateNextSlideStat(index);
      if (stop) return;
    } else if (
      this.fragmentsInsertToInsert().length > this.slidesInsert().length
    ) {
      this.populateNextSlideInsert();
    }
    this.advanceSwiper();
  }

  openSelectChaos(index: number, fieldLabel: string) {
    createNestedDialogModalOptions(this.modalController, GenerateChaosComponent, {
      fieldLabel,
    })
      .then((opts) => this.modalController.create(opts))
      .then((m) => m.present().then(() => m))
      .then((m) => m.onWillDismiss<ChaosModalReturnValue>())
      .then((res) => {
        if (res.data?.role === 'value') {
          if (!res.data.result?.error && res.data?.result) {
            this.updateInsertChaos(index, res.data?.result.value);
          }
        }
      });
  }

  insertCanContinue(slide: InsertSlide, tab: 'value' | 'chaos'): boolean {
    if (tab === 'chaos') {
      return !!(slide.chaos && slide.chaos.length > 0);
    }
    return slide.value !== undefined && slide.value !== null;
  }

  compiledInsert(index: number, value: number) {
    this.slidesInsert.update((val) =>
      val.map((el, i) =>
        i === index ? { ...el, value, type: 'value' as const } : el,
      ),
    );
    if (this.fragmentsInsertToInsert().length > this.slidesInsert().length) {
      this.populateNextSlideInsert();
    }
    this.advanceSwiper();
  }

  compiledInsertChaos(index: number, chaos: string) {
    this.slidesInsert.update((val) =>
      val.map((el, i) =>
        i === index ? { ...el, chaos, type: 'chaos' as const } : el,
      ),
    );
    if (this.fragmentsInsertToInsert().length > this.slidesInsert().length) {
      this.populateNextSlideInsert();
    }
    this.advanceSwiper();
  }

  populateNextSlideStat(index: number) {
    let nextfragment = this.fragmentsSelectStatToInsert().find(
      (el) => el.index > index,
    );
    let nextPath = nextfragment!.path.map((identifier, i) => {
      if (identifier.includes('@')) {
        let previously = Object.entries(this.originalReference()).filter(
          ([key, value]) => +key < nextfragment!.index,
        );
        let yetSelected = previously.find(([key, op]) =>
          op.some(
            (p, j) =>
              j === i &&
              (p.replaceAll('$', '') === identifier.replaceAll('$', '') ||
                p.replaceAll('$', '') ===
                  identifier.replace('@', '*').replaceAll('$', '')),
          ),
        )?.[0];
        if (
          yetSelected !== undefined &&
          this.updatedReference()[yetSelected as any]?.[i]
        ) {
          return (
            (identifier.includes('$') ? '$' : '') +
            this.updatedReference()[yetSelected as any][i].replaceAll('$', '')
          );
        }
      }
      return identifier;
    });
    if (nextPath.every((el) => !(el.includes('@') && el.includes('*')))) {
      this.compiledStat(nextfragment!.index, nextPath);
      return true;
    } else {
      this.slidesSelectStat.update((slides) => [
        ...slides,
        this.fragmentsSelectStatToInsert()[this.slidesSelectStat().length],
      ]);
    }
    return false;
  }

  populateNextSlideInsert() {
    const next = this.fragmentsInsertToInsert()[this.slidesInsert().length];
    if (!next) return;
    this.slidesInsert.update((slides) => [
      ...slides,
      this.slideFromInsertMeta(next),
    ]);
  }

  updateInsertValue(index: number, value: string) {
    this.slidesInsert.update((slides) =>
      slides.map((el, i) => {
        return {
          ...el,
          value: i === index ? (value === '' ? 0 : +value) : el.value,
          type: 'value' as const,
        };
      }),
    );
  }

  updateInsertChaos(index: number, value: string) {
    this.slidesInsert.update((slides) =>
      slides.map((el, i) => {
        return {
          ...el,
          chaos: i === index ? (value ? value : '') : el.chaos,
          type: 'chaos' as const,
        };
      }),
    );
  }

  setInsertTab(index: number, val: string | number | undefined) {
    const tab: 'value' | 'chaos' = val === 'chaos' ? 'chaos' : 'value';
    this.slidesInsertToggle()![index].set(tab);
  }

  insertChaosModalLabel(slide: InsertSlide): string {
    return humanizeFieldName(slide.fieldName) || '';
  }

  complete() {
    this.posthog.action('roll_operation', 'try', {
      operationName: this.selectedOperation()?.name,
      characterId: this.impersonateChar()?.id,
      wizardSteps:
        this.slidesSelectStat().length + this.slidesInsert().length,
    });
    let allStats: { stat: ReferenceToSheetFragment; index: number }[] = [];
    iterateAllReferenceToSheet(
      parseOperation(this.selectedOperation()?.operation.split(';')[0]!),
      (stat, index) => {
        allStats.push({
          stat,
          index,
        });
      },
    );
    let updatedWithIndex = Object.entries(this.updatedReference()).map(
      ([i, el]) => [+i, getReferenceFromStatPath(el)],
    );

    let result = { ...this.selectedOperation()! };
    let compiledOperation = parseOperation(result.operation!.split(';')[0]!);
    let i = 0;
    updateAllReferenceToSheet(compiledOperation, (op) => {
      let res = {
        type: 'referenceToSheet',
        name: allStats.find((el) => el.index === i)?.stat.name,
        value: updatedWithIndex.find((el) => el[0] === i)
          ? updatedWithIndex.find((el) => el[0] === i)![1]
          : allStats.find((el) => el.index === i)?.stat.value,
      } as ReferenceToSheetFragment;
      i = i + 1;
      return Promise.resolve(res);
    }).then((_updated) => {
      const creationSlides = this.slidesInsert().filter(
        (s) => s.phase === 'creation',
      );
      const executionSlides = this.slidesInsert().filter(
        (s) => s.phase === 'execution',
      );
      let creationI = 0;
      let executionI = 0;

      const resolveInsertSlide = (
        slide: InsertSlide,
        el: { name?: string },
      ): ChaosFragment | ValueFragment => {
        if (slide.type === 'chaos') {
          return {
            type: 'chaos',
            value: slide.chaos ?? ';',
            name: el.name,
          };
        }
        return {
          type: 'value',
          value: slide.value ?? 0,
          name: el.name,
        };
      };

      return updateAllInsert(_updated, 'creation', (el) => {
        const slide = creationSlides[creationI++];
        return Promise.resolve(resolveInsertSlide(slide, el));
      })
        .then((afterCreation) =>
          updateAllInsert(afterCreation, 'execution', (el) => {
            const slide = executionSlides[executionI++];
            return Promise.resolve(resolveInsertSlide(slide, el));
          }),
        )
        .then((updated) => {
          result.withStatOperation =
            stringifyOperation(updated).result +
            ';' +
            result.operation!.split(';')[1];
          updateAllReferenceToSheet(updated, (op) => {
            let element = getElementByPath(
              getStatPathFromReference(op.value),
              this.impersonateChar(),
            );
            let value: ValueFragment | ChaosFragment = {
              type: 'value',
              value: 0,
              name: op.name,
            };
            switch (element?.type) {
              case 'number':
                value = {
                  type: 'value',
                  value: element.value?.value ?? 0,
                  name: op.name,
                };
                break;
              case 'pool':
                value = {
                  type: 'value',
                  value: element.value?.value ?? 0,
                  name: op.name,
                };
                break;
              case 'boolean':
                value = {
                  type: 'value',
                  value: element.value?.value ? 1 : 0,
                  name: op.name,
                };
                break;
              case 'chaos':
                value = {
                  type: 'chaos',
                  value: element.value?.value ?? '[]d',
                  name: op.name,
                };
                break;
              default:
                break;
            }
            return Promise.resolve(value);
          }).then((compiled) => {
            result.compiledOperation =
              stringifyOperation(compiled).result +
              ';' +
              result.operation!.split(';')[1];
            this.posthog.action('roll_operation', 'success', {
              operationName: result.name,
              characterId: this.impersonateChar()?.id,
            });
            this.dismiss(result);
          });
        });
    });
  }

  dismiss(value: NamedOperation | undefined) {
    if (value === undefined) {
      this.posthog.event('roll_operation_cancel', {
        operationName: this.selectedOperation()?.name,
        characterId: this.impersonateChar()?.id,
      });
    }
    this.modalController.dismiss(value);
  }
}
